import type { Language } from '@inflearn/i18n-tools';
import { isNil } from 'lodash-es';
import qs, { type StringifiableRecord } from 'query-string';

import { NEXT_PUBLIC_BUSINESS_HOST, NEXT_PUBLIC_MENTORING_SITE_URL } from '../../appConfig';
import { type CommunityPostType, convertPostTypeToPlural } from '../utils/post/postItemType';
import { generateRallitSiteURL, generateSiteURL } from '../utils/url';

export const AUTH_URL = {
  SIGNIN: (options?: { query?: StringifiableRecord; lang?: Language }) => {
    const query = options?.query || {};
    const lang = options?.lang;

    return qs.stringifyUrl({
      url: generateSiteURL('/signin', { useLang: true, specificLang: lang }),
      query,
    });
  },
  SIGNUP: () => generateSiteURL('/signup', { useLang: true }),
  FIND_ID: () => generateSiteURL('/signin/find/id', { useLang: true }),
  FIND_PASSWORD: () => generateSiteURL('/signin/find/password', { useLang: true }),
  VERIFY_EMAIL: () => generateSiteURL('/signup/verify', { useLang: true }),
  INTEGRATED_ACCOUNT_POLICY: () => generateSiteURL('/policy', { useLang: true }),
  INFLEARN_PRIVACY: () => generateSiteURL('/policy/privacy', { useLang: true }),
  INFLEARN_TERMS: () => generateSiteURL('/policy/terms-of-service', { useLang: true }),
  PARTNERS_NEW: () => generateSiteURL('/partners/new', { useLang: true }),
  PARTNERS_TERMS: () => generateSiteURL('/policy/partners-terms', { useLang: true }),
  PARTNERS_OPERATION: () => generateSiteURL('/policy/partners-operation', { useLang: true }),
  PARTNERS_GUIDE: () => generateSiteURL('/policy/partners-guide', { useLang: true }),
  PARTNERS_LANDING: () => generateSiteURL('/tag-curation/common_tag/partners', { useLang: false }),
  PARTNERS_LANDING_VI: () =>
    generateSiteURL('/tag-curation/common_tag/partners-vi', { useLang: false }),
};

export const MY_URL = {
  ACCOUNT: () => generateSiteURL('/my/settings/account', { useLang: true }),
  NOTIFICATION: () => generateSiteURL('/my/settings/notification', { useLang: true }),
  DEVICE: () => generateSiteURL('/my/settings/account/device', { useLang: true }),
  NOTES: () => generateSiteURL('/my/notes', { useLang: true }),
  QUESTION: () => generateSiteURL('/my/posts/question', { useLang: true }),
  CHAT: () => generateSiteURL('/my/posts/chat', { useLang: true }),
  STUDY: () => generateSiteURL('/my/posts/study', { useLang: true }),
  PROJECT: () => generateSiteURL('/my/posts/project', { useLang: true }),
  ORDERS: () => generateSiteURL('/my/orders', { useLang: true }),
  LIKES: () => generateSiteURL('/my/likes', { useLang: true }),
  POSTS: () => generateSiteURL('/my/posts', { useLang: true }),
  COURSES: () => generateSiteURL('/my/courses', { useLang: true }),
  ARCHIVED_COURSES: () => generateSiteURL('/my/archived-courses', { useLang: true }),
  LIST: () => generateSiteURL('/my/list', { useLang: true }),
  LIST_ID: (id: number) => generateSiteURL(`/my/list/${id}`, { useLang: true }),
  ACTIVITY_LOGS: () => generateSiteURL('/my/activity-logs', { useLang: true }),
  VOUCHERS: () => generateSiteURL('/my/vouchers', { useLang: true }),
  CERTIFICATES: () => generateSiteURL('/my/certificates', { useLang: true }),
  MENTORINGS: () => generateSiteURL('/my/mentorings', { useLang: false }),
  PARTNERS: () => generateSiteURL('/my/partners', { useLang: true }),
  PARTNERS_LINKS: () => generateSiteURL('/my/partners-links', { useLang: true }),
  GIFTS_RECEIVED: () => generateSiteURL('/my/gifts/received', { useLang: true }),
  GIFTS_SENT: () => generateSiteURL('/my/gifts/sent', { useLang: true }),
  DEVICE_VERIFY_REQUEST: () => generateSiteURL('/my/device/verify/request', { useLang: true }),
  DEVICE_VERIFY_CODE: () => generateSiteURL('/my/device/verify/code', { useLang: true }),
  CLIPS: () => generateSiteURL('/my/clips', { useLang: false }),
};

export const USERS_URL = {
  HOME: ({
    userId,
    slug,
    lang,
  }: {
    userId: string;
    slug?: string;
    lang?: Language;
  }) => {
    if (slug) {
      return generateSiteURL(`/users/${userId}/@${encodeURIComponent(slug)}`, {
        useLang: true,
        specificLang: lang,
      });
    }

    return generateSiteURL(`/users/${userId}`, {
      useLang: true,
      specificLang: lang,
    });
  },
  MY_HOME: () => generateSiteURL(`/users/me`, { useLang: true }),
  DASHBOARD: ({ userId, lang }: { userId: string; lang?: Language }) =>
    generateSiteURL(`/users/${userId}/dashboard`, {
      useLang: true,
      specificLang: lang,
    }),
  MY_DASHBOARD: () => generateSiteURL(`/users/me/dashboard`, { useLang: true }),
  COURSES: (userId: string) => generateSiteURL(`/users/${userId}/courses`, { useLang: true }),
  ROADMAPS: (userId: string) => generateSiteURL(`/users/${userId}/roadmaps`, { useLang: true }),
  COURSE_REVIEWS: (userId: string) =>
    generateSiteURL(`/users/${userId}/reviews`, { useLang: true }),
  CLIPS: (userId: string) => generateSiteURL(`/users/${userId}/clips`, { useLang: false }),
  POSTS: (userId: string) => generateSiteURL(`/users/${userId}/posts`, { useLang: true }),
  BLOGS: (userId: string) => generateSiteURL(`/users/${userId}/blogs`, { useLang: true }),
};

export const COURSE_URL = {
  COURSE_DASHBOARD: (slug: string) =>
    generateSiteURL(`/course/${slug}/dashboard`, { useLang: true }),
  COURSE_DETAIL: (slug: string) => generateSiteURL(`/course/${slug}`, { useLang: true }),
  CHALLENGE_DASHBOARD: (slug: string) =>
    generateSiteURL(`/challenge/${slug}/dashboard`, { useLang: false }),
  CHALLENGE_DETAIL: (slug: string) => generateSiteURL(`/challenge/${slug}`, { useLang: false }),
  COURSES: (queryObject?: Record<string, string>) => {
    const queryString = isNil(queryObject) ? '' : `?${qs.stringify(queryObject)}`;

    return generateSiteURL(`/courses` + queryString, {
      useLang: true,
    });
  },
  OFFLINE_DETAIL: (slug: string) => generateSiteURL(`/course/offline/${slug}`, { useLang: false }),
  GIFTS_RECEIVED_DETAIL: ({ code }: { code: string }) =>
    generateSiteURL(`/gifts/receive?code=${code}`, { useLang: true }),
  GIFTS_SENT_DETAIL: ({ giftId }: { giftId: number }) =>
    generateSiteURL(`/gifts/${giftId}/detail`, { useLang: true }),
  CLIPS: () => {
    return generateSiteURL(`/clips`, {
      useLang: false,
    });
  },
  CLIP_EDIT_CLIP_ID: ({ clipId }: { clipId: number }) => {
    return generateSiteURL(`/clip/edit/${clipId}`, {
      useLang: false,
    });
  },
  CLIP_CLIP_ID: ({ clipId }: { clipId: number }) => {
    return generateSiteURL(`/clip/${clipId}`, {
      useLang: false,
    });
  },
};

export const UCC_URL = {
  NOTES_ID: (courseNoteId: number) => generateSiteURL(`/notes/${courseNoteId}`, { useLang: true }), // TODO 현재는 해당 패키지에 포함되어 있지만 추후 이관 예정
  COMMUNITY_DETAIL: ({
    type,
    id,
    slug,
  }: {
    type: CommunityPostType;
    id: number;
    slug?: string;
  }) => {
    if (type === 'question') {
      const path = slug
        ? `/community/${convertPostTypeToPlural(type)}/${id}/${slug}`
        : `/community/${convertPostTypeToPlural(type)}/${id}`;

      return generateSiteURL(path, {
        useLang: true,
      });
    }

    const path = slug
      ? `/${convertPostTypeToPlural(type)}/${id}/${slug}`
      : `/${convertPostTypeToPlural(type)}/${id}`;

    return generateSiteURL(path);
  },
  COMMUNITY_DETAIL_COMMENT: ({
    type,
    id,
    commentId,
    slug,
  }: {
    type: CommunityPostType;
    id: number;
    commentId: number;
    slug?: string;
  }) => {
    if (type === 'question') {
      const path =
        (slug
          ? `/community/${convertPostTypeToPlural(type)}/${id}/${slug}`
          : `/community/${convertPostTypeToPlural(type)}/${id}`) + `?focusComment=${commentId}`;

      return generateSiteURL(path, {
        useLang: true,
      });
    }

    return generateSiteURL(`/${convertPostTypeToPlural(type)}/${id}/comment/${commentId}`);
  },
};

export const INFLEARN_URL = {
  COMMUNITY_REVIEWS_REVIEW_ID: (reviewId: string) =>
    generateSiteURL(`/community/reviews/${reviewId}`, { useLang: true }),
  BLOG_DETAIL: (id: number) => generateSiteURL(`/blogs/${id}`),
  COURSE_UNIT: ({ slug, unitId }: { slug: string; unitId?: number }) =>
    generateSiteURL(`/course/${slug}/unit/${unitId || 'first'}`, {
      useLang: true,
    }),
  COURSES_LECTURE: ({
    courseId,
    unitId,
  }: {
    courseId: number;
    unitId?: number;
  }) =>
    generateSiteURL(
      qs.stringifyUrl({
        url: `/courses/lecture`,
        query: { unitId: unitId || 'first', courseId },
      }),
      {
        useLang: true,
      },
    ),
  ROADMAPS_DETAIL: (id: number) => generateSiteURL(`/roadmaps/${id}`),
  // 커뮤니티
  COMMUNITY_QUESTIONS: () => generateSiteURL('/community/questions'),
  COMMUNITY_CHATS: () => generateSiteURL('/community/chats'),
  COMMUNITY_CHATS_ID: (id: number | string) => generateSiteURL(`/chats/${id}`, { useLang: true }),
  COMMUNITY_STUDIES: () => generateSiteURL('/community/studies'),
  COMMUNITY_STUDIES_ID: (id: number | string) =>
    generateSiteURL(`/studies/${id}`, { useLang: true }),
  COMMUNITY_PROJECTS: () => generateSiteURL('/community/projects'),
  COMMUNITY_PROJECTS_ID: (id: number | string) =>
    generateSiteURL(`/projects/${id}`, { useLang: true }),
  // 유저 마이페이지
  CARTS: () => generateSiteURL('/carts', { useLang: true }),
  MESSAGES: () => generateSiteURL('/user/messages'),
  MY_MENTORINGS: () => generateSiteURL('/user/mentorings'),
  MY_INQUIRIES: () => generateSiteURL('/user/inquiries'),
  MY_COUPONS: () => generateSiteURL('/user/coupons'),
  MY_ROADMAPS: () => generateSiteURL('/user/roadmaps/enrolled'),
  CERTIFICATE: () => generateSiteURL('/certificate'),
  NOT_FOUND: () => generateSiteURL('/404'),
  ORDER_DETAIL: (id: number) => generateSiteURL(`/orders/${id}/detail`, { useLang: true }),
  CLIP_ORDER_DETAIL: (id: number) =>
    generateSiteURL(`/orders/${id}/clip-detail`, { useLang: true }),
  SURVEY: (courseId: number | string) => generateSiteURL(`/survey-offline/${courseId}`),
  SURVEY_READ_ONLY: ({
    courseId,
    userId,
  }: {
    courseId: number | string;
    userId: number | string;
  }) => generateSiteURL(`/survey-offline/${courseId}/read-only/${userId}`),
  COURSE_OFFLINE_DETAIL: (slug: string) => generateSiteURL(`/course/offline/${slug}`),
  MAIN: () => generateSiteURL('', { useLang: true }),
  MENTORING_SETTING: (mentorId: number) => generateSiteURL(`/mentors/${mentorId}/view`),
  MENTORING: (mentorId: number) => generateSiteURL(`/mentors?mentor_id=${mentorId}`),
  NEW_BLOG: () => generateSiteURL('/blog/new'),
  ROADMAP_DETAIL: (id: number) => generateSiteURL(`/roadmaps/${id}`),
  // 콘텐츠
  COURSES: () => generateSiteURL('/courses', { useLang: true }),
  ROADMAPS: () => generateSiteURL('/roadmaps'),
  CREATE_ROADMAP: () => generateSiteURL('/roadmaps/new'),
  // 유저 마이페이지
  MY_POINTS: () => generateSiteURL('/user/points'),
  CHANNELTALK: () => generateSiteURL('/channeltalk'),
};

export const INFLEARN_B2B_URL = {
  BIZ_ENROLMENTS: () => generateSiteURL(`/biz-enrolments`),
  ORDERS: (manageGroupId: number) => `${NEXT_PUBLIC_BUSINESS_HOST}/groups/${manageGroupId}/orders`,
  GROUP_MEMBERS: (id?: number) => `${NEXT_PUBLIC_BUSINESS_HOST}/groups/${id}/members`,
};

const INFLEARN_APP = 'inflearn://';

export const INFLEARN_APP_LINK = {
  INTEGRATED_TERMS: `${INFLEARN_APP}integratedTermsOfService`,
  TERMS: `${INFLEARN_APP}termsOfService`,
  PRIVACY_POLICY: `${INFLEARN_APP}privacyPolicy`,
  SIGNUP_SEND_EMAIL: `${INFLEARN_APP}signUpSentEmail`,
};

export const RALLIT_URL = {
  HOME: generateRallitSiteURL(''),
  POSITIONS: generateRallitSiteURL(''),
  POSITION_DETAIL: (id: number) => generateRallitSiteURL(`/positions/${id}`),
  MY_LOG: generateRallitSiteURL('/my/log'),
  RALLIT_TERMS: () => generateRallitSiteURL('/terms-of-service'),
  RALLIT_PROFILE_LIST: () => generateRallitSiteURL('/profile/list'),
};

export const MENTORING_URL = {
  MENTORING_APPLIED_DETAIL: (id: number) =>
    generateSiteURL(`/mentorings/${id}/detail`, {
      useLang: false,
      baseURL: NEXT_PUBLIC_MENTORING_SITE_URL,
    }),
  MENTORING_LIST: () =>
    generateSiteURL('/mentors', {
      useLang: false,
      baseURL: NEXT_PUBLIC_MENTORING_SITE_URL,
    }),
  MENTOR_DETAIL: (id: number) =>
    generateSiteURL(`/mentors/${id}`, {
      useLang: false,
      baseURL: NEXT_PUBLIC_MENTORING_SITE_URL,
    }),
};
